import React, { useEffect } from "react";
import Stack from "@mui/material/Stack";
import Chip from "@mui/material/Chip";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Checkbox from "@mui/material/Checkbox";
import ListItemText from "@mui/material/ListItemText";
import KeyboardArrowDown from "@mui/icons-material/KeyboardArrowDown";
import Lock from "@mui/icons-material/Lock";
import { useMyEntitlements } from "@seabrookstudios/pitch2table";
import { useSnackbar } from "notistack";

/**
 * @template T
 * @param {object} props
 * @param {import("@seabrookstudios/pitch2table-core").AdvancedFilter<T>[]} props.filters
 * @param {Record<string, string[]>} props.selectionsByLabel
 * @param {(updater: import("react").SetStateAction<Record<string, string[]>>) => void} props.onSelectionsByLabelChange
 */
export const AdvancedFilters = ({ filters = [], selectionsByLabel, onSelectionsByLabelChange }) => {
  const { data: entitlements } = useMyEntitlements();
  const { enqueueSnackbar } = useSnackbar();
  const [anchorEl, setAnchorEl] = React.useState(/** @type {HTMLElement|null} */ (null));
  const [openLabel, setOpenLabel] = React.useState(/** @type {string|null} */ (null));

  /**
   * Keep AdvancedFilter selections normalized to match URL storage (UPPERCASE).
   * @param {string} value
   */
  const normalize = (value) => `${value}`.toUpperCase();

  const isOpen = Boolean(anchorEl) && Boolean(openLabel);

  /**
   * @param {string} label
   */
  const selectedFor = (label) => selectionsByLabel[label] || [];

  const close = () => {
    setAnchorEl(null);
    setOpenLabel(null);
  };

  return (
    <Stack direction="row" spacing={1} justifyContent="center" p={1} flexWrap="wrap">
      {filters
        .filter((filter) => (filter.hideOn ? !filter.hideOn.includes("web") : true))
        .filter((filter) => (filter.hideForUser ? !filter.hideForUser(entitlements) : true))
        .map((filter) => {
          const selectedValues = selectedFor(filter.label);
          const isLocked = filter.locked ? filter.locked(entitlements) : false;
          const lockedOptions = new Set(filter.lockedOptions ? filter.lockedOptions(entitlements) : []);

          const open = (e) => {
            if (isLocked) {
              return;
            }

            setAnchorEl(e.currentTarget);
            setOpenLabel(filter.label);
          };

          const toggleValue = (value) => {
            onSelectionsByLabelChange((prev) => {
              const current = prev[filter.label] || [];
              const normalized = normalize(value);
              const next = current.includes(normalized)
                ? current.filter((v) => v !== normalized)
                : [...current, normalized];

              return { ...prev, [filter.label]: next };
            });
          };

          useEffect(() => {
            if (selectedValues.length === 0) {
              return;
            }
            if (!isLocked) {
              return;
            }

            enqueueSnackbar(`Filter ${filter.label} has been cleared. Upgrade to premium to enable filter.`, {
              variant: "warning",
              preventDuplicate: true,
            });

            onSelectionsByLabelChange({ [filter.label]: [] });
          }, [isLocked, selectedValues.length]);

          return (
            <React.Fragment key={filter.label}>
              <Chip
                id={`advanced-filter-${filter.label}`}
                label={selectedValues.length ? `${filter.label} (${selectedValues.length})` : filter.label}
                onClick={open}
                onDelete={open}
                deleteIcon={isLocked ? <Lock /> : <KeyboardArrowDown />}
                variant={selectedValues.length ? "filled" : "outlined"}
                color={selectedValues.length ? "secondary" : "default"}
                disabled={isLocked}
              />
              <Menu
                anchorEl={anchorEl}
                open={isOpen && openLabel === filter.label}
                onClose={close}
                MenuListProps={{ dense: true }}
              >
                {filter.options.map((option) => (
                  <MenuItem key={option} disabled={lockedOptions.has(option)} onClick={() => toggleValue(option)}>
                    <Checkbox size="small" checked={selectedFor(filter.label).includes(normalize(option))} />
                    <ListItemText primary={option} />
                    {lockedOptions.has(option) && <Lock sx={{ ml: 1 }} fontSize="small" />}
                  </MenuItem>
                ))}
              </Menu>
            </React.Fragment>
          );
        })}
    </Stack>
  );
};
